import {
  Button,
  ButtonGroup,
  closeDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {not} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {
  BulkPatchSupplierDiscountGroupsRequest,
  useBulkPatchSupplierDiscountGroupsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getDecimalFromPercentage, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DISCOUNT_TYPE_OPTIONS} from '../../WarehousesSupplierDiscountGroupsDetail/constants/discountTypeOptions';
import {BULK_EDIT_SUPPLIER_DISCOUNT_GROUP} from '../constants/bulkEditSupplierDiscountGroup';
import {WAREHOUSE_SUPPLIER_DISCOUNT_GROUP} from '../constants/warehouseSupplierDiscountGroup';
import {
  BulkEditSupplierDiscountGroupsForm,
  BulkEditSupplierDiscountGroupsFormSchema,
} from '../types/BulkEditSupplierDiscountGroupsForm';

interface BulkEditSupplierDiscountGroupsProps extends RequiredTestIdProps {
  supplierDiscountGroupIds: string[];
}

export function BulkEditSupplierDiscountGroups(props: BulkEditSupplierDiscountGroupsProps) {
  const [bulkEditSupplierDiscountGroups, {isLoading: isSavingChanges}] =
    useBulkPatchSupplierDiscountGroupsMutation();

  const handleSubmit: FormSubmitHandler<BulkEditSupplierDiscountGroupsForm> = async (
    formValues
  ) => {
    const requestBody: BulkPatchSupplierDiscountGroupsRequest = {
      typeOfCalculation: formValues.isDiscountTypeEnabled ? formValues.discountType : null,
      discountPercentage: formValues.isDiscountValueEnabled
        ? getDecimalFromPercentage(formValues.discountValue)
        : null,
      supplierDiscountGroupIds: props.supplierDiscountGroupIds,
    };

    await bulkEditSupplierDiscountGroups(requestBody)
      .unwrap()
      .then(() => closeDialog(BULK_EDIT_SUPPLIER_DISCOUNT_GROUP))
      .then(() => refreshDmsDatagrid(WAREHOUSE_SUPPLIER_DISCOUNT_GROUP, {deselectAll: true}))
      .catch(handleApiError);
  };

  const defaultValues: Partial<BulkEditSupplierDiscountGroupsForm> = {
    isDiscountTypeEnabled: false,
    isDiscountValueEnabled: false,
  };

  return (
    <Form<BulkEditSupplierDiscountGroupsForm>
      experimentalZodSchema={BulkEditSupplierDiscountGroupsFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const dependentFieldsForEditing = formApi.watch([
          'isDiscountTypeEnabled',
          'isDiscountValueEnabled',
        ]);

        const atLeastOneDependentFieldForEditingEnabled = dependentFieldsForEditing.some((field) =>
          isTrue(field)
        );

        const isDiscountTypeEnabled = formApi.watch('isDiscountTypeEnabled');
        const isDiscountValueEnabled = formApi.watch('isDiscountValueEnabled');

        return (
          <VStack spacing={4} data-testid={props['data-testid']}>
            <Box flex={1}>
              <HStack align="center" spacing={5}>
                <Box paddingTop={5}>
                  <FormField
                    control={control}
                    name="isDiscountTypeEnabled"
                    type="switch"
                    data-testid={suffixTestId('inputs.isDiscountTypeEnabled', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    isDisabled={not(isDiscountTypeEnabled)}
                    control={control}
                    name="discountType"
                    type="choice"
                    label={i18n.t('entity.warehouse.labels.discountType')}
                    options={DISCOUNT_TYPE_OPTIONS}
                    menuInPortal
                    data-testid={suffixTestId('inputs.discountType', props)}
                  />
                </Box>
              </HStack>
            </Box>
            <Box flex={1}>
              <HStack align="center" spacing={5}>
                <Box paddingTop={5}>
                  <FormField
                    control={control}
                    name="isDiscountValueEnabled"
                    type="switch"
                    data-testid={suffixTestId('inputs.isDiscountValueEnabled', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    isDisabled={not(isDiscountValueEnabled)}
                    control={control}
                    name="discountValue"
                    type="number"
                    label={i18n.t('entity.warehouse.labels.discountValue')}
                    suffix="%"
                    data-testid={suffixTestId('inputs.discountValue', props)}
                  />
                </Box>
              </HStack>
            </Box>

            <ButtonGroup align="right">
              <Button
                type="button"
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={() => closeDialog(BULK_EDIT_SUPPLIER_DISCOUNT_GROUP)}
                data-testid={suffixTestId('actions.discard', props)}
              />
              <FormButton
                isDisabled={not(atLeastOneDependentFieldForEditingEnabled)}
                isLoading={isSavingChanges}
                control={control}
                type="submit"
                title={i18n.t('general.actions.apply')}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
